import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Stethoscope, UserPlus, X } from 'lucide-react';
import { mensajeError } from '../../lib/api';
import { getMedicosVisitantes, setMedicosVisitantes, strToDate } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

// Los mismos límites que pone el servidor (backend/services/medicos_visitantes.py).
export const MAX_MEDICOS_VISITANTES = 2;
export const NOMBRE_MAX = 80;
export const ESPECIALIDAD_MAX = 60;

/** El texto como lo guarda el servidor: una línea, sin espacios de sobra. */
const limpio = (s) => String(s || '').replace(/\s+/g, ' ').trim();

/** «Dr. X (Oftalmología) · Dra. Y (Cirugía plástica)». */
export function textoMedicos(medicos) {
  return (medicos || [])
    .filter((m) => limpio(m?.nombre))
    .map((m) => (limpio(m.especialidad) ? `${limpio(m.nombre)} (${limpio(m.especialidad)})` : limpio(m.nombre)))
    .join(' · ');
}

/**
 * ¿Se apunta médico visitante en esta agenda? Solo en las que la clínica ha
 * marcado «Admite médico visitante» en Gestión de agendas (administración,
 * 7/10/2026): no va en todas, solo en la consulta y el quirófano de algún
 * médico. Sin el campo, no. El servidor dice lo mismo (422 al guardar).
 */
export function admiteVisitante(agenda) {
  return Boolean(agenda?.admite_visitante);
}

/** «Dra. Ejemplo · ORL, Dr. Otro · Anestesia»: lo corto, para una cabecera. */
export function textoCorto(medicos) {
  return (medicos || [])
    .filter((m) => limpio(m?.nombre))
    .map((m) => (limpio(m.especialidad) ? `${limpio(m.nombre)} · ${limpio(m.especialidad)}` : limpio(m.nombre)))
    .join(', ');
}

/**
 * EL MÉDICO VISITANTE EN LA VISTA DE VARIAS AGENDAS (administración, 7/10/2026)
 * -------------------------------------------------------------------------
 * Bajo el nombre de cada columna, cuando hay varias agendas a la vista (las
 * elegidas o «Todas»): «Visitante: Dra. Ejemplo · ORL» o, con dos,
 * «Visitantes: Dra. Ejemplo · ORL, Dr. Otro · Anestesia». Una línea pequeña
 * que se corta con «…» si no cabe; entera en el título. Solo lectura: se
 * apunta abriendo la agenda suelta. Sin nadie ese día, no pinta nada.
 *
 * Un alto fijo (h-4), el mismo del hueco que dejan las columnas sin visitante
 * cuando otra sí lo tiene (CitasPage), para que las rejillas sigan a la par.
 * Sin elementos absolutos (ni sr-only): en el móvil la fila de columnas se
 * desplaza de lado y uno absoluto podía escaparse del recorte.
 */
export function VisitantesColumna({ medicos, testId }) {
  const lista = (medicos || []).filter((m) => limpio(m?.nombre));
  if (!lista.length) return null;
  const uno = lista.length === 1;
  return (
    <p
      className="relative mb-1 flex h-4 min-w-0 items-center gap-1 overflow-hidden px-1 text-[11px] leading-4 text-[#0E7490]"
      title={`${uno ? 'Médico visitante' : 'Médicos visitantes'}: ${textoMedicos(lista)}`}
      data-testid={testId}
    >
      <Stethoscope className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">
        <span className="font-semibold">{uno ? 'Visitante' : 'Visitantes'}:</span> {textoCorto(lista)}
      </span>
    </p>
  );
}

// Si el 409 llega sin su texto (6/10/2026; el del servidor es este mismo).
const OTRA_PERSONA = 'Otra persona ha cambiado los médicos visitantes de este día: se ha recargado, vuelve a hacer el cambio';

/**
 * Lo leído de un día, en la forma del estado (`porDefecto`: si no trae lista).
 * `admite`: el «Admite médico visitante» que dice el servidor al leer (más
 * reciente que el de la agenda que tiene la pantalla), o null si no lo dice.
 */
function leidoDe(clave, d, porDefecto = [], admitePorDefecto = null) {
  return {
    clave,
    medicos: Array.isArray(d?.medicos) ? d.medicos : porDefecto,
    actualizadoAt: d?.actualizado_at ?? null,
    admite: typeof d?.admite_visitante === 'boolean' ? d.admite_visitante : admitePorDefecto,
    error: false,
  };
}

function diaLegible(fecha) {
  try {
    const s = strToDate(fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch {
    return fecha;
  }
}

/**
 * «MÉDICO VISITANTE» DEL DÍA (administración, 6/10/2026)
 * ------------------------------------------------------
 * «Necesitamos que se añada también en las agendas de consulta de todos los
 * médicos, y que en todas las agendas (tanto quirófano como consulta) se
 * permita registrar hasta 2 médicos visitantes a la vez. Cada registro […]
 * debe incluir el nombre del médico y su especialidad médica.»
 *
 * Arriba de la agenda en vista día: si viene alguien, una etiqueta de color
 * («Médico visitante: Dr. X (Oftalmología) · Dra. Y (Cirugía plástica)») que
 * se ve de un vistazo; si no, un botón discreto para añadirlo. Las dos cosas
 * abren el mismo diálogo, donde cada médico se añade, se cambia o se quita, y
 * cada cosa se guarda al momento (y queda en el rastro por separado).
 *
 * Es de la agenda y el día, no de las citas, y vale igual en consulta que en
 * quirófano: en quirófano va dentro de la barra del día (QuirofanoDiaBar) y no
 * pide el lugar; en consulta, en una barra mínima (`MedicoVisitanteBarra`).
 * Mismos permisos que el resto de la barra: el servidor contesta 403 a quien
 * no puede citar en la agenda, y su mensaje sale tal cual.
 *
 * SIN PERDER A NADIE (revisión del 6/10/2026). Guardar manda la lista entera,
 * así que un puesto con la pantalla vieja borraba al que había apuntado otro.
 * Ahora: se relee al abrir el diálogo; cada guardado dice de qué lista parte
 * (`actualizado_at`) y, si otra persona ha guardado entretanto, el servidor
 * contesta 409: aquí se relee y se dice, y hay que volver a hacer el cambio.
 * Y una respuesta que llega tarde (de otro día, o de una lectura lanzada
 * antes de guardar) no pisa lo que se ve: solo cuenta la última petición.
 *
 * SOLO EN LAS AGENDAS QUE LO ADMITEN (administración, 7/10/2026). No va en
 * todas: solo en las marcadas «Admite médico visitante» en Gestión de agendas
 * (`admiteVisitante`). En las demás no se ofrece (ni el botón discreto); si
 * ese día ya había alguien guardado de antes, se ve igual, como etiqueta que
 * no se pulsa: no se esconde nada en silencio.
 *
 * La marca que manda es la que devuelve el servidor al leer el día
 * (`admite_visitante` del GET, revisión del 7/10/2026): la agenda que tiene
 * la pantalla puede llevar rato cargada y alguien haberla marcado o
 * desmarcado en Gestión de agendas. Mientras el GET no lo diga, la de la
 * agenda.
 *
 * UNO, Y ALGUNA VEZ DOS (7/10/2026). Lo normal es uno por consulta y por
 * quirófano: el diálogo enseña una sola fila (nombre y especialidad) y, para
 * el segundo, un botón pequeño «Añadir otro visitante». Con dos guardados se
 * ven los dos. El tope sigue en dos. Si al apuntar el primero otra persona
 * ya había guardado (409), lo escrito se queda en el formulario, también
 * cuando la lista releída ya trae a alguien (revisión del 7/10/2026).
 *
 * Props: { agenda, date, refreshKey, envolver }
 *  - envolver: (nodo) => nodo, para meter lo que se pinta en una barra solo
 *    cuando hay algo que pintar (la barra de consulta de una agenda sin la
 *    marca, `MedicoVisitanteBarra`).
 */
export default function MedicoVisitanteDia({ agenda, date, refreshKey = 0, envolver = null }) {
  const agendaId = agenda?.id;
  // Lo leído, con la agenda y el día a los que pertenece: al recargar el
  // mismo día (crear o mover una cita) se sigue viendo lo de antes hasta que
  // llega lo nuevo, sin parpadeo; al cambiar de día o de agenda, nada hasta
  // tener lo suyo (nunca los médicos de otro día).
  const clave = `${agendaId}|${date}`;
  // `actualizadoAt`: de qué lista se parte al guardar (el servidor da 409 si
  // ya no es la de ahora). null = ese día aún no tiene lista guardada.
  const [leido, setLeido] = useState({
    clave: null, medicos: [], actualizadoAt: null, admite: null, error: false,
  });
  // La marca del GET de este día, si la ha dicho; si no, la de la agenda.
  const admiteLeido = leido.clave === clave ? leido.admite : null;
  const admite = typeof admiteLeido === 'boolean' ? admiteLeido : admiteVisitante(agenda);
  // Releer a propósito: al abrir el diálogo y tras un 409.
  const [recarga, setRecarga] = useState(0);
  // Cada lectura y cada guardado cogen turno; lo que llega solo se aplica si
  // es la última petición. Así ni la respuesta tardía de otro día ni una
  // lectura lanzada antes de guardar pisan lo que se ve (6/10/2026).
  const turno = useRef(0);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null); // índice del que se cambia, o null
  // «Añadir otro visitante» pulsado: con uno ya apuntado, el formulario del
  // segundo no sale hasta que se pide (7/10/2026).
  const [anadiendo, setAnadiendo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [especialidad, setEspecialidad] = useState('');
  const [saving, setSaving] = useState(false);
  const nombreRef = useRef(null);

  useEffect(() => {
    if (!agendaId || !date) return undefined;
    let cancelled = false;
    const k = `${agendaId}|${date}`;
    turno.current += 1;
    const mio = turno.current;
    const vale = () => !cancelled && turno.current === mio;
    getMedicosVisitantes(agendaId, date)
      .then((d) => {
        if (vale()) setLeido(leidoDe(k, d));
      })
      // Si falla una recarga del mismo día, se queda lo que ya se veía.
      .catch(() => {
        if (vale()) {
          setLeido((prev) => (prev.clave === k && !prev.error ? prev
            : { clave: k, medicos: [], actualizadoAt: null, admite: null, error: true }));
        }
      });
    return () => { cancelled = true; };
  }, [agendaId, date, refreshKey, recarga]);

  // Al cambiar de agenda o de día, el diálogo de la anterior se cierra; y si
  // a la agenda le quitan la marca, también.
  useEffect(() => { setOpen(false); }, [agendaId, date, admite]);

  // Sin poder leerlo no se ofrece nada: un botón que luego no puede guardar
  // confunde más que no verlo.
  if (!agendaId || leido.clave !== clave || leido.error) return null;
  const { medicos } = leido;

  const lleno = medicos.length >= MAX_MEDICOS_VISITANTES;
  const texto = textoMedicos(medicos);
  const pintar = (nodo) => (envolver ? envolver(nodo) : nodo);

  if (!admite) {
    // Agenda sin «Admite médico visitante» (7/10/2026): nada que ofrecer. Si
    // ese día ya había alguien guardado, se ve, pero no se pulsa.
    if (!medicos.length) return null;
    return pintar(
      <span
        title={`Médico visitante: ${texto}. Esta agenda no admite médico visitante: para cambiarlo, actívalo en Gestión de agendas`}
        className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md bg-[#0E7490] px-2.5 py-1 text-[13px] text-white shadow-sm"
        data-testid="medico-visitante"
        data-solo-lectura="1"
      >
        <Stethoscope className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        <span className="font-bold shrink-0">Médico visitante:</span>
        <span className="font-semibold truncate">{texto}</span>
      </span>,
    );
  }

  const vaciarFormulario = () => {
    setEditando(null);
    setAnadiendo(false);
    setNombre('');
    setEspecialidad('');
  };

  const abrir = () => {
    vaciarFormulario();
    setOpen(true);
    // Lo que se ve puede llevar rato en pantalla: se relee al abrir, para
    // partir de la lista de ahora (6/10/2026).
    setRecarga((n) => n + 1);
  };

  const empezarACambiar = (i) => {
    setEditando(i);
    setAnadiendo(false);
    setNombre(medicos[i]?.nombre || '');
    setEspecialidad(medicos[i]?.especialidad || '');
    setTimeout(() => nombreRef.current?.focus(), 0);
  };

  /**
   * Guarda la lista entera, partiendo de la que se ve. Devuelve 'ok',
   * 'conflicto' (otra persona la había cambiado: 409, se relee) o 'error'.
   */
  const guardar = async (lista, mensajeOk) => {
    const k = clave;
    turno.current += 1;
    const mio = turno.current;
    setSaving(true);
    try {
      const d = await setMedicosVisitantes(agendaId, date, lista, leido.actualizadoAt);
      // Guardado de verdad (el toast lo dice aunque ya se vea otro día), pero
      // si entretanto ha salido otra petición, lo que se ve es suyo. Si se
      // ha guardado, la agenda lo admite.
      if (turno.current === mio) setLeido(leidoDe(k, d, lista, true));
      toast.success(mensajeOk);
      return 'ok';
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(mensajeError(e, OTRA_PERSONA));
        setRecarga((n) => n + 1);
        return 'conflicto';
      }
      toast.error(mensajeError(e, 'No se pudo guardar el médico visitante'));
      return 'error';
    } finally {
      setSaving(false);
    }
  };

  const enviarFormulario = async () => {
    if (saving) return;
    const n = limpio(nombre);
    const e = limpio(especialidad);
    if (!n) { toast.error('Escribe el nombre del médico'); return; }
    if (!e) { toast.error('Escribe su especialidad (por ejemplo: Oftalmología)'); return; }
    const repetido = medicos.some((m, i) => i !== editando
      && limpio(m.nombre).toLowerCase() === n.toLowerCase());
    if (repetido) { toast.error(`${n} ya está apuntado ese día`); return; }
    if (editando === null) {
      if (lleno) {
        toast.error(`Como mucho ${MAX_MEDICOS_VISITANTES} médicos visitantes a la vez`);
        return;
      }
      // Con un 409 lo escrito se queda: volver a hacerlo es pulsar «Añadir».
      // Si era el primero y la lista releída ya trae a alguien, el
      // formulario del primero desaparecería con lo escrito dentro: se
      // queda abierto como «Añadir otro visitante» (revisión del 7/10/2026).
      const r = await guardar([...medicos, { nombre: n, especialidad: e }], 'Médico visitante añadido');
      if (r === 'ok') vaciarFormulario();
      else if (r === 'conflicto') setAnadiendo(true);
      return;
    }
    const lista = medicos.map((m, i) => (i === editando ? { nombre: n, especialidad: e } : m));
    // Tras un 409 la lista es otra y el número de fila ya no es el de antes:
    // el cambio se vuelve a empezar desde la lista nueva.
    if (await guardar(lista, 'Médico visitante cambiado') !== 'error') vaciarFormulario();
  };

  const quitar = async (i) => {
    if (saving) return;
    if (await guardar(medicos.filter((_, j) => j !== i), 'Médico visitante quitado') !== 'error') {
      // Si se estaba cambiando ese (o uno de detrás), el formulario ya no vale.
      if (editando !== null) vaciarFormulario();
    }
  };

  const alPulsarIntro = (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); enviarFormulario(); }
  };

  const anadirOtro = () => {
    setEditando(null);
    setAnadiendo(true);
    setNombre('');
    setEspecialidad('');
    setTimeout(() => nombreRef.current?.focus(), 0);
  };

  // Una sola fila por defecto (7/10/2026): sin nadie, el formulario del
  // primero; con uno, el del segundo solo si se pide; con dos, ninguno.
  const formularioVisible = editando !== null
    || (!lleno && (medicos.length === 0 || anadiendo));
  const ofrecerOtro = !formularioVisible && !lleno && medicos.length > 0;
  const tituloFormulario = editando !== null ? 'Cambiar médico visitante'
    : medicos.length ? 'Añadir otro visitante' : 'Añadir médico visitante';

  return pintar(
    <>
      {medicos.length ? (
        <button
          type="button"
          onClick={abrir}
          // Entero también en el título: en el móvil uno largo se corta.
          title={`Médico visitante: ${texto}. Pulsa para añadir, cambiar o quitar`}
          className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md bg-[#0E7490] px-2.5 py-1 text-[13px] text-white shadow-sm hover:bg-[#0B5F75] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E7490] focus-visible:ring-offset-1"
          data-testid="medico-visitante"
        >
          <Stethoscope className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <span className="font-bold shrink-0">Médico visitante:</span>
          <span className="font-semibold truncate">{texto}</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={abrir}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-[#F2F6F4] hover:text-[#13231E] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          data-testid="medico-visitante-anadir"
        >
          <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
          Añadir médico visitante
        </button>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) vaciarFormulario(); }}>
        <DialogContent
          className="max-w-md"
          data-testid="medico-visitante-dialog"
          onOpenAutoFocus={(e) => {
            // Con hueco para otro, el cursor al nombre: es a lo que se viene.
            if (!lleno && nombreRef.current) {
              e.preventDefault();
              nombreRef.current.focus();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Médico visitante — {agenda.name}</DialogTitle>
            <DialogDescription>
              {diaLegible(date)}. Quien viene ese día a la consulta o al quirófano, con su
              especialidad. Lo normal es uno; como mucho, {MAX_MEDICOS_VISITANTES}.
            </DialogDescription>
          </DialogHeader>

          {medicos.length > 0 && (
            <ul className="space-y-1.5" data-testid="medico-visitante-lista">
              {medicos.map((m, i) => (
                <li
                  key={`${m.nombre}-${i}`}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${editando === i ? 'border-[#0E7490] bg-[#0E7490]/[0.06]' : 'border-[#D9E3DD]'}`}
                  data-testid={`medico-visitante-fila-${i}`}
                >
                  <Stethoscope className="w-3.5 h-3.5 text-[#0E7490] shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate" title={textoMedicos([m])}>
                    <span className="font-semibold text-[#13231E]">{m.nombre}</span>
                    {m.especialidad ? (
                      <span className="text-[#13231E]/80"> ({m.especialidad})</span>
                    ) : (
                      <span className="text-amber-700"> · sin especialidad</span>
                    )}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 px-2 text-[#13231E]"
                    onClick={() => empezarACambiar(i)}
                    disabled={saving}
                    data-testid={`medico-visitante-cambiar-${i}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                    Cambiar
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 px-2 text-muted-foreground"
                    onClick={() => quitar(i)}
                    disabled={saving}
                    data-testid={`medico-visitante-quitar-${i}`}
                  >
                    <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                    Quitar
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {formularioVisible && (
            <div className="space-y-3 rounded-md border border-dashed border-[#D9E3DD] p-3" data-testid="medico-visitante-formulario">
              <p className="text-xs font-semibold text-[#13231E]">{tituloFormulario}</p>
              {/* Una fila: nombre y especialidad, uno al lado del otro (en el
                  móvil, uno debajo del otro). */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="medico-visitante-nombre">Nombre del médico</Label>
                  <Input
                    id="medico-visitante-nombre"
                    ref={nombreRef}
                    className="mt-1.5"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    onKeyDown={alPulsarIntro}
                    maxLength={NOMBRE_MAX}
                    autoComplete="off"
                    placeholder="Dra. Nombre Apellido"
                    data-testid="medico-visitante-nombre"
                  />
                </div>
                <div>
                  <Label htmlFor="medico-visitante-especialidad">Especialidad</Label>
                  <Input
                    id="medico-visitante-especialidad"
                    className="mt-1.5"
                    value={especialidad}
                    onChange={(e) => setEspecialidad(e.target.value)}
                    onKeyDown={alPulsarIntro}
                    maxLength={ESPECIALIDAD_MAX}
                    autoComplete="off"
                    placeholder="Por ejemplo: Oftalmología"
                    data-testid="medico-visitante-especialidad"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                {(editando !== null || anadiendo) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={vaciarFormulario}
                    disabled={saving}
                    data-testid="medico-visitante-cancelar"
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={enviarFormulario}
                  disabled={saving}
                  className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                  data-testid="medico-visitante-guardar"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                  {editando !== null ? 'Guardar cambios' : 'Añadir'}
                </Button>
              </div>
            </div>
          )}
          {ofrecerOtro && (
            <div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-[#0E7490] hover:text-[#0B5F75]"
                onClick={anadirOtro}
                disabled={saving}
                data-testid="medico-visitante-otro"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                Añadir otro visitante
              </Button>
            </div>
          )}
          {lleno && editando === null && (
            <p className="text-xs text-muted-foreground" data-testid="medico-visitante-lleno">
              Ya hay {MAX_MEDICOS_VISITANTES} médicos visitantes este día, el máximo.
              Quita uno para añadir otro.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="medico-visitante-cerrar">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>,
  );
}

const barraDeConsulta = (hijo) => (
  <div className="flex min-h-[26px] flex-wrap items-center gap-2" data-testid="consulta-dia-bar">
    {hijo}
  </div>
);

/**
 * La barra mínima del día en las agendas que no son de quirófano (6/10/2026):
 * hasta hoy la consulta no tenía barra del día. Solo lleva el médico visitante
 * y, sin ninguno, es una línea baja con el botón discreto: la vista de
 * siempre no se mueve. (En quirófano va dentro de QuirofanoDiaBar.)
 *
 * En una agenda sin «Admite médico visitante» (7/10/2026) no hay barra: solo
 * aparece si ese día ya había alguien guardado, para verlo.
 */
export function MedicoVisitanteBarra({ agenda, date, refreshKey = 0 }) {
  if (!agenda?.id || agenda?.tipo === 'quirofano') return null;
  if (!admiteVisitante(agenda)) {
    return <MedicoVisitanteDia agenda={agenda} date={date} refreshKey={refreshKey} envolver={barraDeConsulta} />;
  }
  return barraDeConsulta(<MedicoVisitanteDia agenda={agenda} date={date} refreshKey={refreshKey} />);
}
